import { UserRole } from 'src/constants';
import {
  EXPLORE_FOLLOW_UPS,
  FollowUpSet,
  RELATED_FOLLOW_UPS,
  sameText,
} from '../constants';
import APPLICANT_PERSONA from './applicant';
import EMPLOYER_PERSONA from './employer';
import type { AssistantPersona } from './types';

const PERSONAS: Partial<Record<string, AssistantPersona>> = {
  [UserRole.Applicant]: APPLICANT_PERSONA,
  [UserRole.Employer]: EMPLOYER_PERSONA,
};

/**
 * The assistant a user gets as who they are now, or none. A login can switch
 * between applicant and employer; each role gets only its own persona, as the
 * backend gives each only its own audience. A new one is a folder beside
 * `applicant/` and its line in PERSONAS.
 */
export const personaFor = (
  userType: string | null | undefined,
): AssistantPersona | null => (userType && PERSONAS[userType]) || null;

export const isHiddenPath = (persona: AssistantPersona, pathname: string) =>
  persona.focusScreens.some((pattern) => pattern.test(pathname));

export const hasBottomBar = (persona: AssistantPersona, pathname: string) =>
  persona.bottomBarPages.some((pattern) => pattern.test(pathname));

export const hasActionBar = (persona: AssistantPersona, pathname: string) =>
  !!persona.actionBarPages?.some((pattern) => pattern.test(pathname));

export const pageHint = (persona: AssistantPersona, pathname: string) => {
  const hint = persona.pageHints.find((h) => h.pattern.test(pathname));
  return { label: hint?.label ?? null, suggestions: hint?.suggestions ?? [] };
};

/** A page's label as the panel says it: "You're on your profile". */
export const youreOn = (label: string) =>
  `You're on ${label.replace(/^(This|Your) /, (word) => word.toLowerCase())}`;

/**
 * Chips for a reply that came without its own (one from before follow-ups,
 * or the model left the line off): this page's questions, then other
 * features rotating each turn, never one already asked.
 */
export const fallbackFollowUps = (
  persona: AssistantPersona,
  pathname: string,
  asked: string[],
  turn: number,
): FollowUpSet => {
  const fresh = (text: string) => !asked.some((a) => sameText(a, text));
  const options = [
    ...pageHint(persona, pathname).suggestions,
    ...persona.generalSuggestions,
  ]
    .filter(fresh)
    .slice(0, RELATED_FOLLOW_UPS);
  const pool = persona.exploreSuggestions
    .filter((s) => !s.pattern.test(pathname) && fresh(s.text))
    .map((s) => s.text);
  const start = pool.length ? turn % pool.length : 0;
  const explore = [...pool.slice(start), ...pool.slice(0, start)]
    .filter((text) => !options.some((o) => sameText(o, text)))
    .slice(0, EXPLORE_FOLLOW_UPS);
  return { options, explore };
};

export type { AssistantPersona, OrbGreeting } from './types';
